/**
 * Reading real lengths off a model, once somebody has said how big it is.
 *
 * A reconstruction is correct in shape and arbitrary in size, so every number
 * here is `null` until a calibration exists. That is the point of the module:
 * the alternative - returning model-space numbers and letting the screen
 * decide whether to put "mm" after them - is exactly how an arbitrary figure
 * reaches a customer with a unit attached.
 *
 * Conversion happens on the way out and never touches the stored factor.
 * Switching the display between mm, cm and m is two views of one measurement,
 * not two measurements.
 */

import type { ScaleCalibration } from "@/api/types";

export type Unit = "mm" | "cm" | "m";

export const UNITS_IN_METRES: Record<Unit, number> = {
  mm: 0.001,
  cm: 0.01,
  m: 1,
};

export const UNIT_LABELS: Record<Unit, string> = {
  mm: "mm",
  cm: "cm",
  m: "m",
};

/**
 * A model-space length in real units, or `null` when nobody has measured.
 *
 * `null` rather than the raw number. A function called `realLength` that
 * returned model units when uncalibrated would be handing its caller
 * something that looks like a measurement and is not one.
 */
export function realLength(
  modelLength: number,
  calibration: ScaleCalibration | null,
  unit: Unit,
): number | null {
  if (!calibration?.verified || !calibration.scale_factor) return null;
  return (modelLength * calibration.scale_factor) / UNITS_IN_METRES[unit];
}

/**
 * A length as a string, at the precision the reference actually carried.
 *
 * Somebody who typed "125" knows their object to the millimetre; answering
 * them with "84.63 mm" claims a precision their own input did not have. The
 * server derives the decimal count from the reference and sends it, so both
 * ends agree.
 */
export function formatLength(
  modelLength: number,
  calibration: ScaleCalibration | null,
  unit: Unit,
): string | null {
  const value = realLength(modelLength, calibration, unit);
  if (value === null) return null;
  const decimals = calibration?.display_decimals ?? 1;
  return `${value.toLocaleString("tr-TR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} ${UNIT_LABELS[unit]}`;
}

export interface Dimensions {
  width: number;
  height: number;
  depth: number;
  unit: Unit;
}

/**
 * The model's real size from its bounding box, or `null` when uncalibrated.
 *
 * The axis naming follows the glTF frame the viewer already works in - X
 * across, Y up, Z towards the viewer - and is not a claim about the object's
 * own idea of which way is front. A reconstruction does not have one.
 */
export function dimensionsOf(
  boundsMin: readonly [number, number, number],
  boundsMax: readonly [number, number, number],
  calibration: ScaleCalibration | null,
  unit: Unit,
): Dimensions | null {
  if (!calibration?.verified || !calibration.scale_factor) return null;
  const [width, height, depth] = [0, 1, 2].map(
    (axis) => realLength(boundsMax[axis]! - boundsMin[axis]!, calibration, unit)!,
  ) as [number, number, number];
  return { width, height, depth, unit };
}

/** Bounds of a position buffer, in model space. */
export function boundsOf(
  positions: Float32Array,
): { min: [number, number, number]; max: [number, number, number] } {
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i + 2 < positions.length; i += 3) {
    for (let axis = 0; axis < 3; axis += 1) {
      const value = positions[i + axis]!;
      if (value < min[axis]!) min[axis] = value;
      if (value > max[axis]!) max[axis] = value;
    }
  }
  return { min, max };
}

/** The model's diagonal, which the server uses to refuse a too-short reference. */
export function diagonalOf(
  min: readonly [number, number, number],
  max: readonly [number, number, number],
): number {
  return Math.hypot(max[0] - min[0], max[1] - min[1], max[2] - min[2]);
}

/**
 * How far a second known distance is from what the model says it is.
 *
 * The only real check on a calibration. The reference used to establish the
 * scale matches itself by construction - it is what the factor was computed
 * from - so quoting its error as accuracy would be quoting a tautology. An
 * independent distance is the first number that means anything.
 */
export function verificationError(
  modelLength: number,
  expectedLength: number,
  calibration: ScaleCalibration | null,
  unit: Unit,
): { absolute: number; relative: number } | null {
  const measured = realLength(modelLength, calibration, unit);
  if (measured === null || expectedLength <= 0) return null;
  const absolute = measured - expectedLength;
  return { absolute, relative: (absolute / expectedLength) * 100 };
}
